import type { WorkData, WorkProduct } from '../../../../types/Work'
import type { WorkId } from '../../../../types/WorkId'
import type { WorkQuantity } from '../../../../types/WorkQuantity'
import type { WorkDataAction } from './WorkDataAction'
import type { IWorksDataContext } from './WorksDataContext'

export function reducer(state: IWorksDataContext, action: WorkDataAction): IWorksDataContext {
  switch (action.type) {
    case 'UPDATE_QUANTITIES': {
      const newOrUpdatedQty = action.quantities as WorkQuantity
      if (!newOrUpdatedQty) {
        return state
      }
      const newWorksData = createNewWorksData(state.worksData, action.workId, newOrUpdatedQty)
      const isValid = isValidGrandQuantities(newWorksData)
      const newState = {
        ...state,
        isWorkQuantitiesValid: isValid,
        worksData: newWorksData,
      }

      if (action.isPrimaryProject) {
        // Update state based on primary project validity only
        newState.isPrimaryProjectWorkQuantitiesValid = newOrUpdatedQty.isValidQuantity
      }

      return newState
    }
    case 'UPDATE_PRODUCT': {
      const newOrUpdatedProduct = action.productInfo as WorkProduct
      if (!newOrUpdatedProduct) {
        return state
      }
      const newWorksData = createNewWorksData(state.worksData, action.workId, newOrUpdatedProduct)
      const newState = {
        ...state,
        worksData: newWorksData,
      }
      return newState
    }
    case 'RESTORE_WORK': {
      // check if the work exists in deleted
      if (!state.worksData[action.workId]) {
        return state // can't restore if it doesn't exist
      }

      const newWorksData = {
        ...state.worksData,
        [action.workId]: {
          ...state.worksData[action.workId],
          isDeleting: false,
          isDeleted: false,
        } as WorkData,
      }

      const isValid = isValidGrandQuantities(newWorksData)
      return {
        ...state,
        isWorkQuantitiesValid: isValid,
        worksData: newWorksData,
      }
    }
    case 'DELETE_WORK_START': {
      const newWorksData = { ...state.worksData }
      newWorksData[action.workId] = {
        ...newWorksData[action.workId],
        isDeleting: true,
      } as WorkData
      const isValid = isValidGrandQuantities(newWorksData)
      const newState = {
        ...state,
        isWorkQuantitiesValid: isValid,
        worksData: newWorksData,
      }
      return newState
    }
    case 'DELETE_WORK': {
      const deletedWorksData = { ...state.worksData }
      deletedWorksData[action.workId] = {
        ...deletedWorksData[action.workId],
        isDeleting: false,
        isDeleted: true,
      } as WorkData
      const isValid = isValidGrandQuantities(deletedWorksData)
      const newState = {
        ...state,
        isWorkQuantitiesValid: isValid,
        worksData: deletedWorksData,
      }
      return newState
    }
    default:
      return state
  }
}

const isValidGrandQuantities = (worksData: Record<WorkId, WorkData>) => {
  // Shall we check if pricing is available?
  const activeWorkData = Object.values(worksData).filter((workData) => workData.isDeleted !== true && workData.isDeleting !== true)
  if (activeWorkData.length === 0) {
    return false
  }
  return activeWorkData.every((workData) => workData.isValidQuantity)
}

const createNewWorksData = (worksData: Record<WorkId, WorkData>, workId: WorkId, payload: WorkQuantity | WorkProduct) => {
  const defaultWorkData: WorkData = {
    inputQuantity: 0,
    totalQuantity: 0,
    isValidQuantity: false,
    minimumOrderQuantity: 1,
    productInfo: undefined,
  }
  const newWorkData = { ...defaultWorkData, ...worksData[workId], ...payload }
  newWorkData.isDeleted = false
  newWorkData.isDeleting = false
  return { ...worksData, ...{ [workId]: newWorkData } }
}

export default reducer
